import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { FaInfoCircle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { FcCamcorderPro } from "react-icons/fc";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import { Col, Container, Row, Button, Card, Form } from "react-bootstrap";

import Loader from "../../components/Loader";
import CopyRight from "../../components/CopyRight";
import GuideCard from "../../components/GuideCard";
import BottomModal from "../../components/PlanPopup";
import CustomPopUp from "../../components/CustomPopUp";

import FindImei from "../../assets/svg/FindImei.svg";
import CrossIcon from "../../assets/svg/CrossIcon.svg";
import DeleteIcon from "../../assets/svg/DeleteIcon.svg";

import {
  protectFormInitialValues,
  validationSchema,
  defaultViewPlanDetails,
  convertToBase64,
  getPlanList,
  getBrandList,
  openPaymentGateway,
  submitProtectForm,
} from "./ProtectFormData";

import "./index.css";

const ProtectForm = () => {
  const navigate = useNavigate();
  const mediaStreamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const mediaRecorderRef = useRef(null);

  const isSmallScreen = useMediaQuery({ query: "(max-width: 767px)" });
  const isLessThanMd = useMediaQuery({ minWidth: 768 });
  const isMoreThanMd = useMediaQuery({ maxWidth: 768 });

  const [showModal, setShowModal] = useState(false);
  const [isFileSelected, setIsFileSelected] = useState("");
  const [selectedPlan, setSelectedPlan] = useState("protect");
  const [selectedPlanVariantId, setSelectedPlanVariantId] = useState("");
  const [selectedPlanAmount, setSelectedPlanAmount] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [videoRecInBase64, setVideoRecInBase64] = useState("");
  const [videoPreviewStream, setVideoPreviewStream] = useState(null);
  const [loading, setLoading] = useState(false);
  const [planDetails, setPlanDetails] = useState([]);
  const [brandList, setBrandList] = useState(null);
  const [selectedPlanData, setSelectedPlanData] = useState(null);
  const [showBottomModal, setShowBottomModal] = useState(false);

  useEffect(() => {
    getPlanList(setPlanDetails, setLoading);
    getBrandList(setBrandList, setLoading);
  }, []);

  useEffect(() => {
    if (planDetails?.length > 0) {
      setSelectedPlan(planDetails[0]?.plan_Description);
    }
  }, [planDetails]);

  const handleCardClick = (planData) => {
    setSelectedPlan(planData?.plan_Description);
    setSelectedPlanVariantId(planData?.plan_VarriantsId);
    setSelectedPlanAmount(planData?.plan_Amount);
    setSelectedPlanData(planData);
  };

  const handlePlanChange = (e) => setSelectedPlan(e.target.value);
  const handleFileDelete = () => setIsFileSelected("");

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      mediaStreamRef.current = stream;
      setVideoPreviewStream(stream);
      mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) recordedChunksRef.current.push(event.data);
      };
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(recordedChunksRef.current, { type: "video/mp4" });
        recordedChunksRef.current = [];
        const base64 = await convertToBase64(blob);
        setVideoRecInBase64(base64);
      };
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch {
      toast.error("Unable to access camera.");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
    setVideoPreviewStream(null);
    setIsRecording(false);
  };

  const handleOpenPaymentGateway = useCallback(
    (userId, dmiDetails) =>
      openPaymentGateway(userId, dmiDetails, videoRecInBase64, setLoading),
    [videoRecInBase64],
  );

  const handleSubmit = (values) =>
    submitProtectForm(
      values,
      { selectedPlanVariantId, selectedPlanAmount, videoRecInBase64 },
      setLoading,
      handleOpenPaymentGateway,
    );

  const RecordButton = ({ justify }) => (
    <div
      className={`container-fluid d-flex justify-content-${justify} mt-1 mb-3`}
    >
      <button
        className="btn responsive-btn"
        onClick={isRecording ? stopRecording : startRecording}
      >
        {isRecording
          ? "Stop Video Recording"
          : videoRecInBase64
            ? "Re-Record Video"
            : "Record Video"}
      </button>
    </div>
  );

  const VideoPreview = () => {
    if (isRecording) {
      return (
        <video
          className="upload-guide-video"
          ref={(el) => {
            if (el) {
              el.srcObject = videoPreviewStream;
              el.play();
            }
          }}
        />
      );
    }
    if (videoRecInBase64) {
      return (
        <video
          controls
          className="upload-guide-video"
          src={`data:video/mp4;base64,${videoRecInBase64}`}
        />
      );
    }
    return (
      <div
        className="d-flex justify-content-center align-items-center upload-card-container"
        style={{
          width: "100%",
          height: "200px",
          borderRadius: "10px",
          border: "2px solid white",
        }}
      >
        <FcCamcorderPro size={30} />
        <h5 className="text-secondary mt-1">Record and upload your videos.</h5>
      </div>
    );
  };

  return (
    <Container fluid className="gradient-bg">
      <div className="auth-wrapper text-center">
        {loading ? (
          <Loader loading={loading} />
        ) : (
          <div style={{ width: "96%", height: "100%", marginTop: "48px" }}>
            <div style={{ width: "100%", alignItems: "center" }}>
              <GuideCard selectedData={selectedPlanData} />
            </div>

            <div style={{ width: "98%", margin: "0 auto" }}>
              <Formik
                validationSchema={validationSchema}
                initialValues={protectFormInitialValues}
                onSubmit={handleSubmit}
              >
                {({
                  values,
                  errors,
                  handleChange,
                  handleSubmit,
                  setFieldValue,
                }) => (
                  <FormikForm noValidate onSubmit={handleSubmit}>
                    <h4 className="personal-details-heading-txt">
                      Personal Details
                    </h4>

                    <Row className="mt-3">
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-4">
                          <Field
                            type="text"
                            name="name"
                            placeholder="Name"
                            className="form-control font-family-Manrope"
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                              textAlign: "left",
                            }}
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-4">
                          <Field
                            type="email"
                            name="email"
                            placeholder="Email ID"
                            className="form-control"
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                            }}
                          />
                          <ErrorMessage
                            name="email"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-4">
                          <Field
                            className="form-control"
                            type="tel"
                            placeholder="Mobile"
                            name="mobile"
                            value={values.mobile}
                            onChange={handleChange}
                            maxLength={10}
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                            }}
                          />
                          <ErrorMessage
                            name="mobile"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group position-relative mb-3">
                          <Field
                            className="form-control"
                            type="tel"
                            placeholder="IMEI"
                            name="imeiNumber"
                            value={values.imeiNumber}
                            onChange={handleChange}
                            maxLength={15}
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                              paddingRight: "30px",
                            }}
                          />
                          <FaInfoCircle
                            className="position-absolute"
                            style={{
                              top: "50%",
                              right: "10px",
                              transform: "translateY(-50%)",
                              color: "#F69321",
                            }}
                            onClick={() => setShowModal(true)}
                          />
                          <ErrorMessage
                            name="imeiNumber"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-3">
                          <Form.Group
                            as={Col}
                            md="12"
                            controlId="validationFormik02"
                          >
                            <div
                              style={{ maxHeight: "150px", overflowY: "auto" }}
                            >
                              <Form.Control
                                as="select"
                                name="brandName"
                                value={values.brandName}
                                onChange={handleChange}
                                style={{
                                  border: "none",
                                  borderRadius: 0,
                                  fontSize: "14px",
                                  backgroundColor: "transparent",
                                  borderBottom: "1px solid #BDBDBD",
                                }}
                              >
                                <option value="">Select Brand</option>
                                {brandList?.map((item) => (
                                  <option value={item?.value} key={item?.value}>
                                    {item?.value}
                                  </option>
                                ))}
                              </Form.Control>
                            </div>
                          </Form.Group>
                          <ErrorMessage
                            name="brandName"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-4">
                          <Field
                            type="text"
                            name="modelName"
                            placeholder="Model Name"
                            className="form-control"
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              textAlign: "left",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                              marginTop: "9px",
                            }}
                          />
                          <ErrorMessage
                            name="modelName"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group position-relative">
                          <Field
                            type="tel"
                            name="zipCode"
                            maxLength={6}
                            placeholder="Zipcode"
                            value={values.zipCode}
                            onChange={handleChange}
                            className="form-control mb-4"
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                              paddingRight: "30px",
                            }}
                          />
                          <ErrorMessage
                            name="zipCode"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-4">
                          <Field
                            type="text"
                            name="address"
                            placeholder="Address"
                            value={values?.address}
                            onChange={handleChange}
                            className="form-control"
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                              textAlign: "left",
                            }}
                          />
                          <ErrorMessage
                            name="address"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-4">
                          <Field
                            type="text"
                            name="city"
                            placeholder="City"
                            value={values?.city}
                            onChange={handleChange}
                            className="form-control"
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                              textAlign: "left",
                            }}
                          />
                          <ErrorMessage
                            name="city"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                      <Col lg={4} md={5} sm={11}>
                        <div className="form-group mb-4">
                          <Field
                            type="text"
                            name="state"
                            placeholder="State"
                            value={values?.state}
                            onChange={handleChange}
                            className="form-control"
                            style={{
                              border: "none",
                              borderRadius: 0,
                              fontSize: "14px",
                              backgroundColor: "transparent",
                              borderBottom: "1px solid #BDBDBD",
                              textAlign: "left",
                            }}
                          />
                          <ErrorMessage
                            name="state"
                            component="div"
                            className="text-danger"
                            style={{ fontSize: "12px", textAlign: "left" }}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Plan Selection */}
                    <Row className="mt-1">
                      <h5 className="select-plan-heading-txt">Select Plan</h5>
                      {planDetails?.map((planData) => (
                        <Col
                          lg={4}
                          md={5}
                          sm={11}
                          key={planData?.plan_Description}
                        >
                          <Card
                            className={`p-3 ${selectedPlan === planData?.plan_Description ? "border-primary" : ""}`}
                            style={{
                              background:
                                selectedPlan === planData?.plan_Description
                                  ? "#00B2E433"
                                  : "#00B2E41A",
                              borderRadius: "15px",
                              cursor: "pointer",
                              marginTop: "10px",
                            }}
                            onClick={() => {
                              handleCardClick(planData);
                              setFieldValue("plan", planData.plan_Description);
                            }}
                          >
                            <Form.Check
                              type="radio"
                              name="plan"
                              onChange={handlePlanChange}
                              className="protect-plus-txt"
                              id={planData?.plan_Description}
                              label={planData?.plan_Description}
                              value={planData?.plan_Description}
                              checked={
                                selectedPlan === planData?.plan_Description
                              }
                            />
                            <div className="d-flex justify-content-between align-items-center">
                              <p className="mb-0 coverage-txt">
                                Coverage of up to ₹
                                {planData?.plan_Description === "Protect Plus"
                                  ? "10,000"
                                  : "5,000"}
                              </p>
                              <h5 className="plan-amount-txt">
                                ₹{planData?.plan_Amount}
                              </h5>
                            </div>
                          </Card>
                        </Col>
                      ))}
                    </Row>

                    {/* Upload Recording — desktop */}
                    {isLessThanMd && (
                      <Col
                        lg={8}
                        md={10}
                        sm={11}
                        className="upload-image-container"
                      >
                        <Form.Label
                          className="upload-mobile-image-heading-txt"
                          style={{ width: "100%" }}
                        >
                          Upload Recording
                        </Form.Label>
                        {isFileSelected ? (
                          <Card
                            className="text-center p-4 d-flex justify-content-center align-items-center"
                            style={{
                              border: "2px dotted grey",
                              borderRadius: "15px",
                            }}
                          >
                            <img
                              className="me-3"
                              alt="upload file"
                              src={isFileSelected}
                              style={{ width: 120 }}
                            />
                            <img
                              src={DeleteIcon}
                              className="mt-3"
                              alt="DeleteIcon"
                              onClick={handleFileDelete}
                              style={{ width: 30, cursor: "pointer" }}
                            />
                          </Card>
                        ) : (
                          <Card
                            className="text-center p-4 d-flex justify-content-center align-items-center"
                            style={{
                              border: "2px dotted grey",
                              borderRadius: "15px",
                            }}
                          >
                            <div className="align-items-center w-100">
                              <VideoPreview />
                            </div>
                            <RecordButton justify="center" />
                          </Card>
                        )}
                      </Col>
                    )}

                    {/* Upload Recording — mobile */}
                    {isMoreThanMd && (
                      <Col
                        lg={8}
                        md={10}
                        sm={11}
                        className="upload-image-container"
                      >
                        <Form.Label
                          className="upload-mobile-image-heading-txt"
                          style={{ width: "100%" }}
                        >
                          Upload Recording
                        </Form.Label>
                        <Card
                          className="text-center p-3 d-flex justify-content-center align-items-flex-start"
                          style={{
                            border: "1px dotted rgba(0, 178, 228, 1)",
                            borderRadius: "15px",
                            width: "100%",
                          }}
                        >
                          {isFileSelected ? (
                            <Row
                              style={{ height: "140px" }}
                              className="d-flex justify-content-center align-items-center"
                            >
                              <Col xs={10}>
                                <img
                                  alt="Selected File"
                                  src={isFileSelected}
                                  style={{ width: 40 }}
                                />
                              </Col>
                              <Col xs={2}>
                                <img
                                  src={DeleteIcon}
                                  alt="DeleteIcon"
                                  onClick={handleFileDelete}
                                  style={{ width: 30, cursor: "pointer" }}
                                />
                              </Col>
                            </Row>
                          ) : (
                            <div className="align-items-center w-100">
                              <VideoPreview />
                            </div>
                          )}
                          <RecordButton justify="end" />
                        </Card>
                      </Col>
                    )}

                    {/* Terms */}
                    <Form.Group
                      controlId="formBasicCheckbox"
                      className="mb-5 text-left"
                      style={{ textAlign: "left", marginTop: 10 }}
                    >
                      <Form.Check
                        type="checkbox"
                        label={
                          <p className="term-condition-txt-two">
                            I agree to the{" "}
                            <a
                              href="/assets/TermsAndConditions.pdf"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="term-condition-txt"
                            >
                              Terms & Conditions
                            </a>
                          </p>
                        }
                        name="termsAgreed"
                        checked={values.termsAgreed}
                        onChange={handleChange}
                      />
                      <ErrorMessage
                        name="termsAgreed"
                        component="div"
                        className="text-danger"
                        style={{ fontSize: "12px", marginTop: "-15px" }}
                      />
                    </Form.Group>

                    {/* Desktop buttons */}
                    <Row className="d-none d-md-flex">
                      <Col lg={4} md={5} sm={11}>
                        <Button
                          variant="outline-dark"
                          className="cancel-btn w-100 p-2"
                          onClick={() => navigate("/")}
                        >
                          Cancel
                        </Button>
                      </Col>
                      <Col lg={4} md={5} sm={11}>
                        <div className="custom-button2">
                          <Button
                            type="submit"
                            variant="dark"
                            className="proceed-to-pay-btn w-100 p-2"
                          >
                            Proceed To Pay
                          </Button>
                        </div>
                      </Col>
                    </Row>

                    {/* Mobile sticky footer */}
                    <Col lg={4} md={5} sm={11} xs={11}>
                      {isSmallScreen && (
                        <div className="fixed-buttons-container align-items-center">
                          <div className="d-flex justify-content-center">
                            <Col xs="auto" className="text-center">
                              <div className="label-container">
                                <div className="label1">Buy Screen Protect</div>
                                <div className="label2">
                                  <Button
                                    variant=""
                                    className="proceed-button"
                                    onClick={() => setShowBottomModal(true)}
                                  >
                                    View Plan Details
                                  </Button>
                                </div>
                              </div>
                            </Col>
                          </div>
                          <div className="label3">
                            <Button
                              type="submit"
                              variant="dark"
                              className="pay-btn p-2"
                            >
                              Pay ₹ {selectedPlanData?.plan_Amount || "699"}
                            </Button>
                          </div>
                        </div>
                      )}
                    </Col>
                  </FormikForm>
                )}
              </Formik>
            </div>

            <div style={{ width: "100%", marginBottom: 20 }}>
              <CopyRight />
            </div>
          </div>
        )}
      </div>

      <BottomModal
        show={showBottomModal}
        handleClose={() => setShowBottomModal(false)}
        planDetails={selectedPlanData ?? defaultViewPlanDetails}
      />

      <CustomPopUp
        show={showModal}
        PopUpIcon={FindImei}
        ClosePopUpIcon={CrossIcon}
        heading="Find IMEI Number"
        handleClose={() => setShowModal(false)}
        description="Dial *#06# to check the IMEI details of the mobile device you wish to insure."
      />
    </Container>
  );
};

export default ProtectForm;
